/**
 * Format a date string as a relative time (e.g., "2m ago", "3h ago", "5d ago")
 * Falls back to short date format for older dates.
 */
export function formatRelativeTime(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;

  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

/**
 * Format a date string as a short date (e.g., "Jan 15, 2025")
 */
export function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

/**
 * Format a date string with date and time (e.g., "Jan 15, 2025, 2:30 PM")
 */
export function formatDateTime(dateString: string): string {
  return new Date(dateString).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

/**
 * Format a game date with weekday and time (e.g., "Sat, Mar 15 at 7:30 PM")
 * Omits year since games are typically current season.
 */
export function formatGameDateTime(dateString: string): string {
  const d = new Date(dateString);
  const date = d.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
  const time = d.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
  });
  return `${date} at ${time}`;
}

/**
 * Decompose milliseconds into days, hours, minutes, seconds.
 * Useful for building countdown displays.
 */
export function decomposeDuration(ms: number): { days: number; hours: number; minutes: number; seconds: number } {
  const totalSeconds = Math.floor(ms / 1000);
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  return { days, hours, minutes, seconds };
}

/**
 * Format a compact countdown string for card-level display.
 * Returns empty string if <= 0 or > 7 days.
 * Examples: "2d 5h", "3h 42m", "15m"
 */
export function formatCardCountdown(ms: number): string {
  if (ms <= 0) return '';
  const { days, hours, minutes } = decomposeDuration(ms);
  const remainingHours = hours;

  if (days > 7) return '';
  if (days > 0) return `${days}d ${remainingHours}h`;
  const totalHours = days * 24 + hours;
  if (totalHours > 0) return `${totalHours}h ${minutes}m`;
  return `${minutes}m`;
}
